import { ChangeEvent, useEffect, useState } from "react";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import SceneInit from "../services/PoseSceneInitialization";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import Select from "./Select";
import { SelectOption } from "../interfaces/Toolbar";
import { PoseViewProps } from "../interfaces/View3D";
import { Limb, Pose, Rotation } from "../interfaces/Choreography";
import { tangentBodyPartNameToNumber, tangentBodyPartNumberToName } from "../services/VisServices";
import * as THREE from "three";


function findJointByUuid(
  model: THREE.Object3D,
  uuidToFind: string,
) {
    model.traverse(function (child) {
      if (child instanceof THREE.Mesh) {
        //console.log(child)
        if (child.uuid == uuidToFind) {
          
          return child;
        }
      }
    });
    return model;
}


function PoseView(props: PoseViewProps) {
  const [selectedModel, setSelectedModel] = useState<null | THREE.Group>(null);

  const [maleModel, setMaleModel] = useState<null | THREE.Group>(null);
  const [femaleModel, setFemaleModel] = useState<null | THREE.Group>(null);

  const [scene, setScene] = useState<null | SceneInit>(null);

  var Loaders = {
    FBX: new FBXLoader(),
  };

  useEffect(() => {
    let localScene = new SceneInit("pose-view-canvas");

    localScene.initialize();
    localScene.transformControls!.addEventListener("objectChange", () => {
      setSelectedPoseOption(selectPoseOptions.length - 1);
    });
    //localSceneInit.animate();

    Loaders.FBX.load("../ybot.fbx", (model) => {
      console.log(model)
      setMaleModel(model);
      model.position.y = -100;

      let limb = model.getObjectByName("mixamorigSpine");
      localScene.transformControls!.attach(limb!);

      setSelectedModel(model);

      let poseToLoad = props.savedPoses[0];
      if (props.selectedDancerOption) {
        const couple = props.choreo.patterns[props.selectedPattern].positions[Math.floor((props.selectedDancerOption - 1) / 2)];
        const dancer = couple.position[(props.selectedDancerOption - 1) % 2];
  
        poseToLoad = dancer.pose;
        if (poseToLoad.limbs.length === 0) {
          if (couple.joint) {
            console.log("Give that man a pose")
            poseToLoad = props.savedPoses[1];
          }else {
            poseToLoad = props.savedPoses[0];
          }
        }
      }


    poseToLoad.limbs.forEach((limb) => {
      const modelLimb = model!.getObjectByName(limb.name);
      modelLimb!.rotation.x = limb.rotation.x;
      modelLimb!.rotation.y = limb.rotation.y;
      modelLimb!.rotation.z = limb.rotation.z;
    });
    localScene.scene!.add(model);
  });

  Loaders.FBX.load("../xbot.fbx", (model) => {
    setFemaleModel(model);
    model.position.y = -100;
    model.visible = false;

    let limb = model.getObjectByName("mixamorigSpine");
    localScene.transformControls!.attach(limb!);

    let poseToLoad = props.savedPoses[0];
    if (props.selectedDancerOption) {
      const couple = props.choreo.patterns[props.selectedPattern].positions[Math.floor((props.selectedDancerOption - 1) / 2)];
      const dancer = couple.position[(props.selectedDancerOption - 1) % 2];

      poseToLoad = dancer.pose;
      if (poseToLoad.limbs.length === 0) {
        if (couple.joint) {
          console.log("Give that woman a pose.")
          poseToLoad = props.savedPoses[2];
        }else {
          poseToLoad = props.savedPoses[0];
        }
      }
    }


    poseToLoad.limbs.forEach((limb) => {
      const modelLimb = model!.getObjectByName(limb.name);
      modelLimb!.rotation.x = limb.rotation.x;
      modelLimb!.rotation.y = limb.rotation.y;
      modelLimb!.rotation.z = limb.rotation.z;
    });

    localScene.scene!.add(model);
  });

  setScene(localScene);
  }, []);


  /**
   * Callback function when the selected model changes (when the user picks the dancer of another sex)
   */
  useEffect(() => {
    if (selectedModel && maleModel && femaleModel && scene) {

      let workModel = selectedModel;
      //Check if the selected dancer is a woman and change the model to work on
      if (props.selectedDancerOption > -1) {
        if (props.selectedDancerOption % 2 === 0) {
          workModel = femaleModel;
        }else {
          workModel = maleModel;
        }
      }

      if (workModel === maleModel) {
        maleModel!.visible = true;
        femaleModel!.visible = false;
        scene.transformControls!.detach();
        let limb = maleModel!.getObjectByName(
          "mixamorig" + selectLimbOptions[selectedLimbOption].key
        );
        scene.transformControls!.attach(limb!);
      } else {
        maleModel!.visible = false;
        femaleModel!.visible = true;
        scene.transformControls!.detach();
        let limb = femaleModel!.getObjectByName(
          "mixamorig" + selectLimbOptions[selectedLimbOption].key
        );
        scene.transformControls!.attach(limb!);
      }

      //If a dancer gets selected, we need to show his/her partner if they dance as a couple
      if (props.selectedDancerOption > 0) {
        const coupleData = props.choreo.patterns[props.selectedPattern].positions[Math.floor((props.selectedDancerOption - 1) / 2)];
        const dancerId = (props.selectedDancerOption - 1) % 2;
        const partnerId = (dancerId === 0) ? 1 : 0;
        const partnerData = coupleData.position[partnerId];


        let partnerModel: THREE.Group;

        if (coupleData.joint) {
          //Make both models visible
          femaleModel!.visible = true;
          maleModel!.visible = true;

          //Reset position and rotation of both models
          femaleModel!.position.z = 0;
          femaleModel!.rotation.y = 0;
          maleModel!.position.z = 0;
          maleModel!.rotation.y = 0;

          //Displace and rotate the partners model
          if (workModel === maleModel) {
            femaleModel!.position.z += 40;
            femaleModel!.rotation.y = Math.PI;
            partnerModel = femaleModel;
          } else {
            maleModel!.position.z += 40;
            maleModel!.rotation.y = Math.PI;
            partnerModel = maleModel;
          }

          //At the program start, the partner model has no pose assigned
          //We need to do this manually
          let partnerPoseToLoad = partnerData.pose;
          if (partnerPoseToLoad.limbs.length === 0) {
            if (coupleData.joint) {
              partnerPoseToLoad = props.savedPoses[partnerId+1];
            }else {
              //Load standard pose
              partnerPoseToLoad = props.savedPoses[0];
            }
          }

          //Apply pose to load onto the partners model
          partnerPoseToLoad.limbs.forEach((limb) => {
            const modelLimb = partnerModel!.getObjectByName(limb.name);
            modelLimb!.rotation.x = limb.rotation.x;
            modelLimb!.rotation.y = limb.rotation.y;
            modelLimb!.rotation.z = limb.rotation.z;
          });
        }     
      }
    }
  }, [selectedModel, props.selectedDancerOption, props.selectedPattern, props.show3DView]);



  const [selectedLimbOption, setSelectedLimbOption] = useState(0);
  const [selectLimbOptions, setSelectLimbOptions] = useState<SelectOption[]>([
    { value: 0, label: "Spine", key: "Spine" },

    { value: 1, label: "Right arm", key: "RightArm" },
    { value: 2, label: "Right forearm", key: "RightForeArm" },
    { value: 3, label: "Right hand", key: "RightHand" },

    { value: 4, label: "Left arm", key: "LeftArm" },
    { value: 5, label: "Left forearm", key: "LeftForeArm" },
    { value: 6, label: "Left hand", key: "LeftHand" },

    { value: 7, label: "Right leg", key: "RightUpLeg" },
    { value: 8, label: "Right knee", key: "RightLeg" },

    { value: 9, label: "Left leg", key: "LeftUpLeg" },
    { value: 10, label: "Left knee", key: "LeftLeg" },
    { value: 11, label: "Body (Handle with care)", key: "Hips"},
  ]);
  function onSelectLimbChange(e: ChangeEvent<HTMLSelectElement>) {
    setSelectedLimbOption(+e.currentTarget.value);
  }

  const [selectedPoseOption, setSelectedPoseOption] = useState(0);
  const [selectPoseOptions, setSelectPoseOptions] = useState<SelectOption[]>([
    { value: -1, label: "", key: "-1" },
  ]);
  function onSelectPoseChange(e: ChangeEvent<HTMLSelectElement>) {
    setSelectedPoseOption(+e.currentTarget.value);

    const poseName: string = selectPoseOptions[+e.currentTarget.value].label;

    props.savedPoses.forEach((pose) => {
      if (pose.name === poseName) {
        pose.limbs.forEach((limb) => {
          let modelPart = selectedModel!.getObjectByName(limb.name);
          modelPart!.rotation.x = limb.rotation.x;
          modelPart!.rotation.y = limb.rotation.y;
          modelPart!.rotation.z = limb.rotation.z;
        });
        return;
      }
    });
  }

  /**
   * Updates the dropdown list of saved poses if the user adds a new pose.
   */
  useEffect(() => {
    let options: SelectOption[] = [];
    let id = 0;
    let noOption = { value: id, label: `None`, key: String(id) };
    options.push(noOption);
    id++;
    props.savedPoses.forEach((pose) => {
      let option = { value: id, label: `${pose.name}`, key: String(id) };
      id++;
      options.push(option);
    });

    setSelectPoseOptions(options);
  }, [props.savedPoses]);

  /**
   * Updates the position of the transform controls to the currently selected limb.
   */
  useEffect(() => {
    if (maleModel && scene) {
      scene.transformControls!.detach();
      let limb = selectedModel!.getObjectByName(
        "mixamorig" + selectLimbOptions[selectedLimbOption].key
      );
      scene.transformControls!.attach(limb!);

      //Constrain body rotation
      if (selectedLimbOption === 11) {
        scene.transformControls!.showX = false;
        scene.transformControls!.showZ = false;
      }else {
        scene.transformControls!.showX = true;
        scene.transformControls!.showZ = true;
      }
    }
  }, [selectedLimbOption]);


  //Functions to change the global react state (which means that we change either the pose of one selected dancers, of all ladies/gentlemen or of all dancers)
  function applyPoseToAllDancers() {
    for (
      let dancerId = 1;
      dancerId <= 2 * props.choreo.numCouples;
      dancerId++
    ) {
      const couple = props.choreo.patterns[props.selectedPattern].positions[Math.floor((dancerId - 1) / 2)];
      props.changeDancerPose(props.selectedPattern, dancerId, getCurrentPose());
    }
  }

  function applyPoseToAllGentlemen() {
    for (
      let dancerId = 1;
      dancerId <= 2 * props.choreo.numCouples;
      dancerId++
    ) {
      if (dancerId % 2 === 1) {
        props.changeDancerPose(
          props.selectedPattern,
          dancerId,
          getCurrentPose()
        );
        const couple = props.choreo.patterns[props.selectedPattern].positions[Math.floor((dancerId - 1) / 2)];
      }
    }
  }

  function applyPoseToAllLadies() {
    for (
      let dancerId = 1;
      dancerId <= 2 * props.choreo.numCouples;
      dancerId++
    ) {
      if (dancerId % 2 === 0) {
        props.changeDancerPose(
          props.selectedPattern,
          dancerId,
          getCurrentPose()
        );
        const couple = props.choreo.patterns[props.selectedPattern].positions[Math.floor((dancerId - 1) / 2)];
      }
    }
  }

  function applyPoseToSelectedDancer() {
    props.changeDancerPose(
      props.selectedPattern,
      props.selectedDancerOption,
      getCurrentPose()
    );
    const couple = props.choreo.patterns[props.selectedPattern].positions[Math.floor((props.selectedDancerOption - 1) / 2)];
  }

  function getCurrentPose(): Pose {
    let limbs: Limb[] = [];
    selectLimbOptions.forEach((option) => {
      let modelPart = selectedModel!.getObjectByName(
        "mixamorig" + option.key
      );
      let rotation: Rotation = {
        x: modelPart!.rotation.x,
        y: modelPart!.rotation.y,
        z: modelPart!.rotation.z,
      };
      let limb: Limb = { name: "mixamorig" + option.key, rotation: rotation };
      limbs.push(limb);
    });

    let pose: Pose = { name: newStandardPoseName, limbs: limbs };
    return pose;
  }

  /**
   * Callback function when a new dancer or another pattern gets selected.
   */
  useEffect(() => {
      if (maleModel && femaleModel && scene) {
        let localSelectedModel: THREE.Group;
        if (props.selectedDancerOption % 2 === 1) {
          localSelectedModel = maleModel;
          setSelectedModel(maleModel);
        } else {
          localSelectedModel = femaleModel;
          setSelectedModel(femaleModel);
        }
        let poseToLoad = props.savedPoses[0];


        if (props.selectedDancerOption > 0) {
          const coupleData = props.choreo.patterns[props.selectedPattern].positions[Math.floor((props.selectedDancerOption - 1) / 2)];
          const dancerId = (props.selectedDancerOption - 1) % 2;
          const partnerId = (dancerId === 0) ? 1 : 0;
          const dancerData = coupleData.position[dancerId];
          const partnerData = coupleData.position[partnerId];
  
          poseToLoad = dancerData.pose;
          //const tangentBodyPartToLoad = dancerData.tangentBodyPart;

          //Which pose the character we edit gets
          if (poseToLoad.limbs.length === 0) {
            if (coupleData.joint) {
              poseToLoad = props.savedPoses[dancerId+1];
            }else {
              //Load standard pose
              poseToLoad = props.savedPoses[0];
            }
          }

          //Which pose the partner gets 
          let partnerPoseToLoad = partnerData.pose;
          if (partnerPoseToLoad.limbs.length === 0) {
            if (coupleData.joint) {
              partnerPoseToLoad = props.savedPoses[1];
            }else {
              //Load standard pose
              partnerPoseToLoad = props.savedPoses[0];
            }
          }

          //Load the partners pose onto the partners model
          partnerPoseToLoad.limbs.forEach((limb) => {
            const modelLimb = localSelectedModel!.getObjectByName(limb.name);
            modelLimb!.rotation.x = limb.rotation.x;
            modelLimb!.rotation.y = limb.rotation.y;
            modelLimb!.rotation.z = limb.rotation.z;
          });

          //const id = tangentBodyPartNameToNumber(tangentBodyPartToLoad, coupleData.joint);
          //setTangentBodyPart(id);
        }


        
        poseToLoad.limbs.forEach((limb) => {
          const modelLimb = localSelectedModel!.getObjectByName(limb.name);
          modelLimb!.rotation.x = limb.rotation.x;
          modelLimb!.rotation.y = limb.rotation.y;
          modelLimb!.rotation.z = limb.rotation.z;
        });
      }
  }, [props.selectedDancerOption, props.selectedPattern]);

  const [newStandardPoseName, setNewStandardPoseName] = useState("");
  function saveCurrentPoseToStandardPoses() {
    if (newStandardPoseName.length > 0) {
      props.addStandardPose(getCurrentPose());
      setShowSuccessMessage(true);
      setTimeout(function () {
        setShowSuccessMessage(false);
      }, 3000);
    } else {
      setShowFailureMessage(true);
      setTimeout(function () {
        setShowFailureMessage(false);
      }, 3000);
    }
  }

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showFailureMessage, setShowFailureMessage] = useState(false);
  //const [showTangentBodyPartWarning, setShowTangentBodyPartWarning] = useState(false);

  useEffect(() => {
    if (scene) {
      window.requestAnimationFrame(animate.bind(animate));
      render();
    }
  }, [scene]);

  let [animFrame, setAnimFrame] = useState<null | number>(null);
  function animate() {
    if (props.show3DView) {
      setAnimFrame(window.requestAnimationFrame(animate.bind(animate)));
      scene!.controls!.update();
    }
  }

  useEffect(() => {
    render();
  }, [animFrame]);

  useEffect(() => {
    if (scene) {
      if (props.show3DView) {
        animate();
      }
    }
  }, [props.show3DView]);

  function render() {
    if (scene && props.show3DView) {
      scene.renderer!.render(
        scene.scene as THREE.Scene,
        scene.camera as THREE.Camera
      );
    }
  }


  return (
    <>
    
    <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1 }}>
      <Toast onClose={() => setShowSuccessMessage(false)} show={showSuccessMessage} delay={6000} bg="success text-white" autohide>
          <Toast.Header>
            <strong className="me-auto">Pose saved successfully</strong>
          </Toast.Header>
          <Toast.Body>We saved the pose for you. Please not that a pose must have a unique name and that poses cannot be deleted.</Toast.Body>
      </Toast>

      <Toast onClose={() => setShowFailureMessage(false)} show={showFailureMessage} delay={6000} bg="danger text-white" autohide>
          <Toast.Header>
            <strong className="me-auto">A pose must have a name</strong>
          </Toast.Header>
          <Toast.Body>Please enter a name for the pose into the text input right to Save pose.</Toast.Body>
      </Toast>

      
    </ToastContainer>

      <Container
        style={{ visibility: props.show3DView ? "visible" : "hidden" }}
      >
        <Row className="mb-1">
          <Col sm={3}>
            <Badge bg="secondary" className="me-2">
              1
            </Badge>{" "}
            Load pose
          </Col>
          <Col sm={8}>
            <Select
              value={selectedPoseOption}
              onChange={onSelectPoseChange}
              options={selectPoseOptions}
              renderOnAllDevices={true}
            />
          </Col>
        </Row>

        <Row>
          <Col sm={3}>
            <Badge bg="primary" className="me-2">
              2
            </Badge>{" "}
            Select limb
          </Col>
          <Col sm={8}>
            <Select
              value={selectedLimbOption}
              onChange={onSelectLimbChange}
              options={selectLimbOptions}
              renderOnAllDevices={true}
            />
          </Col>
        </Row>

        <Row className="mb-1">
          <Col sm={3}>
            <Badge bg="secondary" className="me-2">
              3
            </Badge>{" "}
            Save pose
          </Col>
          <Col sm={7}>
            <Form.Group className="mb-2">
              <Form.Control
                value={newStandardPoseName}
                onChange={(e) => setNewStandardPoseName(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col sm={2}>
            <Button onClick={() => saveCurrentPoseToStandardPoses()}>
              Save
            </Button>
          </Col>
        </Row>


        

        <Row className="mb-3">
          <Col sm={3}>
            <Badge bg="primary" className="me-2">
              4
            </Badge>{" "}
            Apply for
          </Col>
          <Col sm={9}>
           
            <Button
              onClick={applyPoseToSelectedDancer}
              disabled={props.selectedDancerOption === 0}
              className="me-2"
            >
              Selected Dancer
            </Button>
            
            <Button onClick={applyPoseToAllDancers} className="me-2">All Dancers</Button>
            
            <Button 
              onClick={applyPoseToAllGentlemen} 
              className="me-2"
              disabled={props.selectedDancerOption === 0 || props.selectedDancerOption % 2 === 0}
            >
              Gentlemen
            </Button>
            <Button 
              onClick={applyPoseToAllLadies} 
              disabled={props.selectedDancerOption === 0 || props.selectedDancerOption % 2 === 1}
            >
              Ladies
            </Button>
            
          </Col>
        </Row>


      </Container>
      <div id="pose-view-wrapper" style={{ width: "100%", height: "100%" }}>
        <canvas
          id="pose-view-canvas"
          style={{
            width: "100%",
            height: "60%",
            visibility: props.show3DView ? "visible" : "hidden",
          }}
        />
      </div>
    </>
  );
}

export default PoseView;
